import {
  useAllCoinsQuery,
  useCoinsQuery,
  useDepositInfoQuery,
  useQuoteAssetsCoinQuery,
  useGasCheckQuery,
} from './api';
import { CoinBalances, CoinDetails, DepositInfo } from './types';
import { QueryReturnType } from 'util/queryReturnType';
import { useActiveExchange } from '../user/use';
import { useBalanceQuery } from '../user/api';

export function useCoinBalances(options?: {
  convert?: boolean;
  refetch?: boolean;
}): QueryReturnType<CoinBalances> {
  const { convert = false, refetch = false } = options || {};
  const exchange = useActiveExchange();
  const skip = !exchange?.internalId || !exchange.activated;
  const result = useCoinsQuery(
    {
      exchangeInternalId: exchange?.internalId,
      convert,
    },
    {
      skip,
      refetchOnMountOrArgChange: refetch || 30,
    },
  );

  if (skip) result.data = {} as CoinBalances;
  return result;
}
export function useQuoteAssetsCoin(
  coinId: string,
): QueryReturnType<Array<string>> {
  const exchange = useActiveExchange();
  return useQuoteAssetsCoinQuery(
    {
      exchangeInternalId: exchange?.internalId,
      coinId,
    },
    { skip: !exchange?.internalId || !coinId },
  );
}

export function useExchangeCoinsList(): QueryReturnType<CoinDetails> {
  const exchange = useActiveExchange();
  return useAllCoinsQuery(exchange?.exchangeType, {
    skip: !exchange?.exchangeType,
  });
}

export function useRefetchBalance() {
  const exchange = useActiveExchange();
  // Trigger balance update to be sure that balance widget matches transactions data
  useBalanceQuery(
    {
      exchangeInternalId: exchange?.internalId,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !exchange?.internalId || !exchange.activated,
    },
  );
}

export function useDepositInfo(
  coinId: string,
  networkId: string,
  depositEnable: boolean,
): QueryReturnType<DepositInfo> {
  const exchange = useActiveExchange();
  return useDepositInfoQuery(
    {
      coinId,
      networkId,
      exchangeId: exchange?.internalId,
    },
    { skip: !coinId || !networkId || !exchange?.internalId || !depositEnable },
  );
}

export function useHasEnoughGas(): {
  isLoading: boolean;
  ok: boolean | undefined;
} {
  const { data: gasCheck, isLoading } = useGasCheckQuery(undefined, {
    refetchOnMountOrArgChange: true,
    pollingInterval: 30_000,
  });
  return {
    isLoading,
    ok: gasCheck?.ok,
  };
}
